import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Label,
  Badge,
  Table,
  Spinner,
  Button,
  Progress,
} from "reactstrap";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Pusher from "pusher-js";
import Breadcrumb from "../../../layout/breadcrumb";
import withReducer from "../../../store/withReducer";
import productReducer from "../../manage-product/store";
import CommonDataTable from "../../../share-components/table/commonDataTable";
import PreLoader from "../../../share-components/ui/preLoader";
import { host } from "../../../utils/api";
import { getGoddivaUsProducts } from "../store/goddivaUsProductSlice";
import {
  getGoddivaUsProductVariantsApi,
  resyncGoddivaUsProductApi,
  syncGoddivaUsImagesApi,
  bulkResyncGoddivaUsProductsApi,
  getGoddivaUsBulkStatusApi,
} from "../../../services/manage-product/goddivaUsProductService";

// Per-tenant Pusher app keys (mirrors src/layout/header/desktopNotification.jsx).
const pusherKeys = [
  { domain: "localhost", appKey: "c8813d7053d8e190fb8e" },
  { domain: "cgqa.mycitygoddess.com", appKey: "c8813d7053d8e190fb8e" },
  { domain: "cg.mycitygoddess.com", appKey: "40a16e808dc166ccd733" },
  { domain: "tg.tradegala.net", appKey: "c1fbe1a82ea0261600c5" },
  { domain: "tgqa.tradegala.net", appKey: "c1fbe1a82ea0261600c5" },
  { domain: "mpqa.mygoddiva.com", appKey: "efc217b7c701f1b4b37f" },
  { domain: "uat.mygoddiva.com", appKey: "efc217b7c701f1b4b37f" },
  { domain: "mp.mygoddiva.com", appKey: "33b7f1cf4c7cf49972bc" },
  { domain: "jtqa.mycitygoddess.com", appKey: "4e98d6e5e82244aeaa01" },
  { domain: "mpqa.gkfashionltd.com", appKey: "8e5612821f6d0ec77ed9" },
  { domain: "mp.gkfashionltd.com", appKey: "c84250949ffb47e664bb" },
  { domain: "mpnew.mygoddiva.com", appKey: "33b7f1cf4c7cf49972bc" },
];

const dataMenu = [
  { type: 0, text: "Products" },
  { type: 1, text: "Goddiva US Products" },
];

const imageOptions = [
  { value: "all", label: "All" },
  { value: "synced", label: "Synced" },
  { value: "partial", label: "Partial" },
  { value: "missing", label: "Missing" },
];
const inventoryOptions = [
  { value: "all", label: "All" },
  { value: "matched", label: "Matched" },
  { value: "mismatch", label: "Mismatch" },
];
const mappingOptions = [
  { value: "all", label: "All" },
  { value: "mapped", label: "Mapped" },
  { value: "not_mapped", label: "Not synced" },
];
const ukStatusOptions = [
  { value: "all", label: "All" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
  { value: "draft", label: "Draft" },
];
const usStatusOptions = [
  { value: "all", label: "All" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
  { value: "draft", label: "Draft" },
  { value: "not_synced", label: "Not synced" },
];

// US Shopify status -> display label (active == "Published" so both columns read alike).
const usStatusLabel = (status) => {
  if (!status) return "Not synced";
  const map = { active: "Published", archived: "Archived", draft: "Draft" };
  return map[status] || status.charAt(0).toUpperCase() + status.slice(1);
};

const selectStyles = {
  control: (base) => ({ ...base, minHeight: 38 }),
};

const money = (v, symbol) =>
  v === null || v === undefined || v === "" ? "—" : `${symbol}${Number(v).toFixed(2)}`;

const prettyLabel = (status) => {
  const labels = {
    synced: "Synced",
    failed: "Failed",
    pending: "Pending",
    not_synced: "Not synced",
    matched: "Matched",
    mismatch: "Mismatch",
  };
  return labels[status] || (status ? status.replace(/_/g, " ") : "Not synced");
};

const imageBadge = (status, us, local) => {
  const map = { synced: "success", partial: "warning", missing: "danger" };
  return (
    <Badge color={map[status] || "secondary"}>
      {us}/{local}
    </Badge>
  );
};

const inventoryBadge = (status) => {
  const map = { matched: "success", mismatch: "danger", not_synced: "secondary" };
  return <Badge color={map[status] || "secondary"}>{prettyLabel(status)}</Badge>;
};

// Active/published statuses use a light chip; archived stands out with a dark chip.
const statusChip = (label, archived) =>
  archived ? (
    <Badge color="dark">{label}</Badge>
  ) : (
    <Badge color="light" className="text-dark">
      {label}
    </Badge>
  );

/**
 * Expanded row: variant-level UK<->US detail. Fetched lazily on expand.
 */
const VariantDetail = ({ data }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getGoddivaUsProductVariantsApi(data.product_id)
      .then((res) => {
        if (active) setRows(res.response?.data || []);
      })
      .catch(() => {
        if (active) setRows([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [data.product_id]);

  if (loading) {
    return (
      <div className="p-3">
        <Spinner size="sm" /> Loading variants…
      </div>
    );
  }

  if (!rows.length) {
    return <div className="p-3 text-muted">No variant data.</div>;
  }

  return (
    <div className="p-3" style={{ background: "#fafafa" }}>
      <Table size="sm" bordered responsive className="mb-0">
        <thead>
          <tr>
            <th>UK SKU</th>
            <th>US SKU</th>
            <th>Colour / Size</th>
            <th>UK RRP</th>
            <th>UK Sale</th>
            <th>Rate</th>
            <th>US RRP</th>
            <th>US Sale</th>
            <th>UK Qty</th>
            <th>Buffer</th>
            <th>US Qty</th>
            <th>Inventory</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((v, i) => (
            <tr key={i}>
              <td>{v.uk_sku || "—"}</td>
              <td>
                {v.us_sku || "—"}
                {!v.mapped && (
                  <Badge color="secondary" className="ml-1">
                    not mapped
                  </Badge>
                )}
              </td>
              <td>
                {v.colour || "—"} / {v.uk_size || "—"}
                {v.us_size ? ` → ${v.us_size}` : ""}
              </td>
              <td>{money(v.uk_rrp, "£")}</td>
              <td>{money(v.uk_sale, "£")}</td>
              <td>×{v.conversion_rate}</td>
              <td>{money(v.us_rrp, "$")}</td>
              <td>{money(v.us_sale, "$")}</td>
              <td>{v.uk_qty}</td>
              <td>−{v.buffer}</td>
              <td>{v.us_qty === null || v.us_qty === undefined ? "—" : v.us_qty}</td>
              <td>
                {v.inventory_match === null ? (
                  <Badge color="secondary">n/a</Badge>
                ) : v.inventory_match ? (
                  <Badge color="success">✓ match</Badge>
                ) : (
                  <Badge color="danger">⚠ expected {v.expected_us_qty}</Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const GoddivaUsProducts = () => {
  const dispatch = useDispatch();
  const { data, pagination, conversionRate, buffer, loading } = useSelector(
    ({ product }) => product.goddivaUsProduct
  );

  const [ukSku, setUkSku] = useState("");
  const [usSku, setUsSku] = useState("");
  const [imageStatus, setImageStatus] = useState(imageOptions[0]);
  const [inventoryStatus, setInventoryStatus] = useState(inventoryOptions[0]);
  const [mappingStatus, setMappingStatus] = useState(mappingOptions[0]);
  const [ukStatus, setUkStatus] = useState(ukStatusOptions[0]);
  const [usStatus, setUsStatus] = useState(usStatusOptions[0]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [firstLoad, setFirstLoad] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingText, setSyncingText] = useState("");
  // Bumped after a manual sync to re-run the fetch effect below with the CURRENT filters
  // (avoids the stale-closure reload that ignored the active filters).
  const [refreshKey, setRefreshKey] = useState(0);

  // ---- bulk sync ----
  const [selectedRows, setSelectedRows] = useState([]);
  // "Select all matching across pages" (Gmail-style) — sends filters instead of ids.
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  // Toggled to force react-data-table-component to clear its row selection.
  const [clearSelectedToggle, setClearSelectedToggle] = useState(false);
  // Current/last bulk run; status 'processing' shows the progress bar and locks the button.
  const [bulkRun, setBulkRun] = useState(null);
  const bulkRunActive = bulkRun?.status === "processing";

  const currentFilters = {
    uk_sku: ukSku,
    us_sku: usSku,
    image_status: imageStatus.value,
    inventory_status: inventoryStatus.value,
    mapping_status: mappingStatus.value,
    uk_status: ukStatus.value,
    us_status: usStatus.value,
  };

  // Debounced auto-fetch — no Search/Reset buttons (consistent with the rest of the app).
  useEffect(() => {
    const handler = setTimeout(() => {
      dispatch(
        getGoddivaUsProducts({
          uk_sku: ukSku,
          us_sku: usSku,
          image_status: imageStatus.value,
          inventory_status: inventoryStatus.value,
          mapping_status: mappingStatus.value,
          uk_status: ukStatus.value,
          us_status: usStatus.value,
          page,
          per_page: perPage,
        })
      ).finally(() => setFirstLoad(false));
    }, 400);
    return () => clearTimeout(handler);
  }, [
    dispatch,
    ukSku,
    usSku,
    imageStatus,
    inventoryStatus,
    mappingStatus,
    ukStatus,
    usStatus,
    page,
    perPage,
    refreshKey,
  ]);

  const onResync = (row) => {
    setSyncingText("Syncing product to US…");
    setSyncing(true);
    resyncGoddivaUsProductApi(row.product_id)
      .then((res) => {
        const body = res.response || {};
        if (body.success) {
          toast.success(body.message || "Product synced to US successfully");
        } else {
          toast.error(body.message || "Sync failed");
        }
      })
      .catch(() => toast.error("Sync failed"))
      .finally(() => {
        setSyncing(false);
        setRefreshKey((k) => k + 1);
      });
  };

  const onSyncImages = (row) => {
    setSyncingText("Syncing images to US…");
    setSyncing(true);
    syncGoddivaUsImagesApi(row.product_id)
      .then((res) => {
        const body = res.response || {};
        if (body.success) {
          toast.success(body.message || "Images synced to US successfully");
        } else {
          toast.error(body.message || "Image sync failed");
        }
      })
      .catch(() => toast.error("Image sync failed"))
      .finally(() => {
        setSyncing(false);
        setRefreshKey((k) => k + 1);
      });
  };

  const clearSelection = () => {
    setSelectedRows([]);
    setSelectAllMatching(false);
    setClearSelectedToggle((t) => !t);
  };

  // Tracks the run id whose "completed" we've already reacted to, so a run only fires
  // its completion toast/refresh once.
  const handledCompletionRef = useRef(null);

  // Single place to apply a run-status update (from the mount restore or a Pusher event).
  const applyRunUpdate = (run) => {
    if (!run) return;
    setBulkRun(run);
    if (run.status === "completed" && handledCompletionRef.current !== run.id) {
      handledCompletionRef.current = run.id;
      toast.success(
        `Bulk sync finished — ${run.completed} synced` +
          (run.failed ? `, ${run.failed} failed` : "")
      );
      clearSelection();
      setRefreshKey((k) => k + 1); // refresh the list so "Synced at" updates
    }
  };

  // A new page or a filter change invalidates any cross-page selection.
  useEffect(() => {
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ukSku,
    usSku,
    imageStatus,
    inventoryStatus,
    mappingStatus,
    ukStatus,
    usStatus,
    page,
    perPage,
  ]);

  // Restore the latest bulk-run status on mount. An already-completed run is shown but
  // NOT treated as a fresh completion (don't toast for an old run).
  useEffect(() => {
    let mounted = true;
    getGoddivaUsBulkStatusApi()
      .then((res) => {
        if (!mounted) return;
        const run = res.response?.run || null;
        if (run?.status === "completed") handledCompletionRef.current = run.id;
        setBulkRun(run);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  // Live progress over Pusher. The progress event carries the run counters; completion
  // toasts, refreshes the list, and unlocks the page (de-duped via handledCompletionRef).
  useEffect(() => {
    const domainName = window.location.hostname;
    const pusherKey = pusherKeys.find((el) => el.domain === domainName);
    if (!pusherKey) return undefined;

    let active = true;
    const token = localStorage.getItem("token");
    const pusher = new Pusher(pusherKey.appKey, {
      cluster: "eu",
      auth: {
        headers: { Accept: "application/json", Authorization: "Bearer " + token },
      },
      authEndpoint: host + "broadcasting/auth",
    });
    const channel = pusher.subscribe("goddiva-us-bulk-sync");
    channel.bind("progress", (data) => {
      if (active) applyRunUpdate(data);
    });

    return () => {
      active = false;
      channel.unbind_all();
      pusher.unsubscribe("goddiva-us-bulk-sync");
      pusher.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startBulkSync = () => {
    if (bulkRunActive) {
      toast.error("A bulk sync is already running.");
      return;
    }
    const payload = selectAllMatching
      ? { select_all: true, filters: currentFilters }
      : { product_ids: selectedRows.map((r) => r.product_id) };

    if (!selectAllMatching && !payload.product_ids.length) {
      toast.error("Select at least one product.");
      return;
    }

    setSyncingText("Queuing bulk sync…");
    setSyncing(true);
    bulkResyncGoddivaUsProductsApi(payload)
      .then((res) => {
        const body = res.response || {};
        if (body.success) {
          toast.success(body.message || "Bulk sync queued");
          if (body.run) setBulkRun(body.run);
          clearSelection();
        } else {
          toast.error(body.message || "Could not start bulk sync");
          if (body.run) setBulkRun(body.run);
        }
      })
      .catch(() => toast.error("Could not start bulk sync"))
      .finally(() => setSyncing(false));
  };

  const headerColumns = useMemo(
    () => [
      {
        name: "",
        width: "56px",
        cell: (row) =>
          row.thumbnail ? (
            <img
              src={row.thumbnail}
              alt=""
              style={{ width: 40, height: 50, objectFit: "cover", borderRadius: 4 }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : null,
      },
      {
        name: "Product",
        wrap: true,
        cell: (row) => (
          <div className="py-1">
            <div style={{ fontWeight: 600 }}>
              US: {row.us_name}
              {row.code ? ` [${row.code}]` : ""}
            </div>
            <div className="text-muted" style={{ fontSize: 11 }}>
              UK: {row.uk_name}
            </div>
            <div className="text-muted" style={{ fontSize: 11 }}>
              UK handle: {row.uk_handle || "—"}
            </div>
            <div className="text-muted" style={{ fontSize: 11 }}>
              US handle: {row.us_handle || "—"}
            </div>
          </div>
        ),
      },
      {
        name: "UK Status",
        width: "120px",
        center: true,
        cell: (row) => statusChip(row.uk_status, row.uk_status === "Archived"),
      },
      {
        name: "US Status",
        width: "120px",
        center: true,
        cell: (row) =>
          row.us_status
            ? statusChip(usStatusLabel(row.us_status), row.us_status === "archived")
            : <Badge color="secondary">Not synced</Badge>,
      },
      {
        name: "Images",
        width: "90px",
        center: true,
        cell: (row) => imageBadge(row.image_status, row.us_images, row.local_images),
      },
      {
        name: "Inventory",
        width: "110px",
        center: true,
        cell: (row) => inventoryBadge(row.inventory_status),
      },
      {
        name: "Synced at",
        width: "170px",
        center: true,
        cell: (row) =>
          row.synced_at ? (
            // Already formatted by the backend in the app timezone — print it as-is.
            <span>{row.synced_at}</span>
          ) : (
            <span style={{ color: "#e4566e", fontWeight: 600 }}>Not synced</span>
          ),
      },
      {
        name: "Actions",
        width: "100px",
        center: true,
        cell: (row) => {
          const ukArchived = row.uk_status === "Archived";
          const usArchived = row.us_status === "archived";
          // No image sync for archived products. Re-sync only makes sense when there's
          // something to reconcile — i.e. not when both UK and US are already archived.
          const showResync = !(ukArchived && usArchived);
          const showImages = !ukArchived;

          if (!showResync && !showImages) {
            return <span className="text-muted">—</span>;
          }

          return (
            <div>
              {showResync && (
                <a
                  onClick={() => onResync(row)}
                  title="Re-sync product to US"
                  style={{ cursor: "pointer" }}
                >
                  <i
                    className="fa fa-refresh"
                    style={{ width: 25, fontSize: 16, padding: 11, color: "rgb(40, 167, 69)" }}
                  />
                </a>
              )}
              {showImages && (
                <a
                  onClick={() => onSyncImages(row)}
                  title="Sync images to US"
                  style={{ cursor: "pointer" }}
                >
                  <i
                    className="fa fa-picture-o"
                    style={{ width: 25, fontSize: 16, padding: 11, color: "rgb(208,179,29)" }}
                  />
                </a>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  if (firstLoad) {
    return <PreLoader />;
  }

  return (
    <Fragment>
      {syncing && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255,255,255,0.7)",
            zIndex: 2000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
          <div className="mt-3" style={{ fontWeight: 600 }}>
            {syncingText || "Syncing…"}
          </div>
        </div>
      )}
      <Breadcrumb title="Goddiva US Products" data={dataMenu} />
      <Container fluid={true} className="product-wrapper">
        <Card>
          <CardBody>
            <Row>
              <Col md="3" className="mb-2">
                <Label>UK SKU</Label>
                <Input
                  value={ukSku}
                  placeholder="Search UK SKU"
                  onChange={(e) => {
                    setUkSku(e.target.value);
                    setPage(1);
                  }}
                />
              </Col>
              <Col md="3" className="mb-2">
                <Label>US SKU</Label>
                <Input
                  value={usSku}
                  placeholder="Search US SKU"
                  onChange={(e) => {
                    setUsSku(e.target.value);
                    setPage(1);
                  }}
                />
              </Col>
              <Col md="3" className="mb-2">
                <Label>UK Status</Label>
                <Select
                  styles={selectStyles}
                  value={ukStatus}
                  options={ukStatusOptions}
                  onChange={(opt) => {
                    setUkStatus(opt);
                    setPage(1);
                  }}
                />
              </Col>
              <Col md="3" className="mb-2">
                <Label>US Status</Label>
                <Select
                  styles={selectStyles}
                  value={usStatus}
                  options={usStatusOptions}
                  onChange={(opt) => {
                    setUsStatus(opt);
                    setPage(1);
                  }}
                />
              </Col>
              <Col md="3" className="mb-2">
                <Label>Images</Label>
                <Select
                  styles={selectStyles}
                  value={imageStatus}
                  options={imageOptions}
                  onChange={(opt) => {
                    setImageStatus(opt);
                    setPage(1);
                  }}
                />
              </Col>
              <Col md="3" className="mb-2">
                <Label>Inventory</Label>
                <Select
                  styles={selectStyles}
                  value={inventoryStatus}
                  options={inventoryOptions}
                  onChange={(opt) => {
                    setInventoryStatus(opt);
                    setPage(1);
                  }}
                />
              </Col>
              <Col md="3" className="mb-2">
                <Label>Mapping</Label>
                <Select
                  styles={selectStyles}
                  value={mappingStatus}
                  options={mappingOptions}
                  onChange={(opt) => {
                    setMappingStatus(opt);
                    setPage(1);
                  }}
                />
              </Col>
            </Row>
            {conversionRate != null && (
              <div className="mt-2 text-muted" style={{ fontSize: 12 }}>
                USD conversion rate: <b>×{conversionRate}</b> · Inventory buffer:{" "}
                <b>−{buffer}</b>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            {/* Live progress for an in-flight bulk run (survives refresh via status API). */}
            {bulkRunActive && (
              <div
                className="mb-3 p-3"
                style={{ background: "#eef4ff", borderRadius: 6 }}
              >
                <div className="d-flex justify-content-between mb-1">
                  <b>Bulk sync in progress…</b>
                  <span>
                    {(bulkRun.completed || 0) + (bulkRun.failed || 0)} / {bulkRun.total}
                    {bulkRun.failed ? ` (${bulkRun.failed} failed)` : ""}
                  </span>
                </div>
                <Progress
                  value={
                    bulkRun.total
                      ? Math.round(
                          (((bulkRun.completed || 0) + (bulkRun.failed || 0)) /
                            bulkRun.total) *
                            100
                        )
                      : 0
                  }
                />
                <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                  Inventory and images are syncing in the background. You can leave this
                  page; another bulk sync is blocked until this finishes.
                </div>
              </div>
            )}

            {/* Selection action bar (hidden while a run is active). */}
            {!bulkRunActive && (selectAllMatching || selectedRows.length > 0) && (
              <div
                className="mb-3 p-2 d-flex align-items-center flex-wrap"
                style={{ background: "#f6f6f6", borderRadius: 6, gap: 12 }}
              >
                <span>
                  {selectAllMatching
                    ? `All ${pagination?.total || 0} products matching the filters are selected.`
                    : `${selectedRows.length} product(s) selected.`}
                </span>

                {!selectAllMatching &&
                  selectedRows.length > 0 &&
                  selectedRows.length === data.length &&
                  (pagination?.total || 0) > data.length && (
                    <a
                      style={{ cursor: "pointer", color: "#204081", fontWeight: 600 }}
                      onClick={() => setSelectAllMatching(true)}
                    >
                      Select all {pagination?.total} matching
                    </a>
                  )}

                <Button color="primary" size="sm" onClick={startBulkSync} disabled={syncing}>
                  Sync selected (inventory + images)
                </Button>
                <Button color="link" size="sm" onClick={clearSelection}>
                  Clear
                </Button>
              </div>
            )}

            <div className="table-responsive product-table">
              <CommonDataTable
                headerColumns={headerColumns}
                gridData={data}
                keyField="product_id"
                noAction={true}
                progressPending={loading}
                progressComponent={
                  <div className="p-4 text-center">
                    <Spinner color="primary" />
                  </div>
                }
                selectableRows
                onSelectedRowsChange={({ selectedRows: rows }) => setSelectedRows(rows)}
                clearSelectedRows={clearSelectedToggle}
                expandableRows
                expandableRowsComponent={VariantDetail}
                paginationServer
                paginationTotalRows={pagination?.total || 0}
                paginationPerPage={perPage}
                paginationRowsPerPageOptions={[20, 50, 100]}
                onChangePage={(p) => setPage(p)}
                onChangeRowsPerPage={(pp) => {
                  setPerPage(pp);
                  setPage(1);
                }}
              />
            </div>
          </CardBody>
        </Card>
      </Container>
    </Fragment>
  );
};

export default withReducer([{ product: productReducer }])(GoddivaUsProducts);
